import { lazy } from 'react';

import { useAppConfig } from '#/config/AppConfigContext';
import { RouteModule } from '#/horizon/components/RouteModule';
import { getServerContext } from '#/horizon/server-context';
import { useI18n } from '#/i18n/hooks/useI18n';
import { I18N_LOCALES } from '#/i18n/types/I18nLocale';
import type { LayoutHandleProps } from '#/layouts/Layout/LayoutHandleProps';
import { listingApiIsomorphicFetchOrg } from '#/modules/listing/orgs/api/listingApiFetchOrg';
import type { viewOrgReferenceRoute } from '#/modules/listing/routing/listing.routes';
import { ClientOnlyLazySearchOrgsRefinements } from '#/modules/search/orgs/components/Refinements/ClientOnlyLazySearchOrgsRefinements';
import { userDashboardApiIsomorphicFetchListingExtras } from '#/modules/userDashboard/api/listingExtras/userDashboardApiFetchListingExtras';
import { LazyNotFoundPage } from '#/pages/NotFound/LazyNotFoundPage';
import type { HorizonAppRouteLoaderArgs } from '#/routing/classes/types/AppRouteLoaderArgs';
import type { HorizonAppRouteComponentProps } from '#/routing/types/HorizonAppRouteComponentProps';
import type { HorizonAppRouteShouldRevalidateFunctionArgs } from '#/routing/types/HorizonAppRouteShouldRevalidateFunctionArgs';
import { idealistNotFound } from '#/routing/utils/idealistNotFound';
import { LISTING_TYPE_HUMAN_NAME_MAP_TITLE } from '#/utils/constants/general/listingTypeHumanNameMapTitle';
import { imageUrlForOgMeta } from '#/utils/filestack';

import { ViewOrgPage } from './ViewOrgPage';

const LazyUnpublishedOrgPage = lazy(() =>
  import('#/modules/listing/orgs/pages/unpublished/UnpublishedOrgPage').then(
    (m) => ({ default: m.UnpublishedOrgPage }),
  ),
);

export const handle = {
  defaultSearchType: 'ORG',
  showSearchRefinements: true,
  showDesktopSearch: true,
  searchRefinements: <ClientOnlyLazySearchOrgsRefinements />,
} satisfies LayoutHandleProps;

export function shouldRevalidate({
  currentUrl,
  nextUrl,
}: HorizonAppRouteShouldRevalidateFunctionArgs<typeof viewOrgReferenceRoute>) {
  return currentUrl.pathname !== nextUrl.pathname;
}

export async function loader({
  request,
  context,
  params: { orgIdDashSlug },
}: HorizonAppRouteLoaderArgs<typeof viewOrgReferenceRoute>) {
  const { serverContext } = getServerContext(context);

  const [orgId] = orgIdDashSlug.split('-');

  try {
    const [org, listingExtras] = await Promise.all([
      listingApiIsomorphicFetchOrg({ request, context })(orgId),
      serverContext.hasNoCacheCookie
        ? userDashboardApiIsomorphicFetchListingExtras({
            request,
            context,
          })([orgId])
        : null,
    ]);

    if (!org) return idealistNotFound(null);

    if (org.type === 'UNPUBLISHED_ORG') {
      return idealistNotFound({
        type: 'UNPUBLISHED_ORG',
        unpublishedOrg: org,
        handle: {
          hideFooterDivider: 'hide-footer-divider-if-logged-out',
        } satisfies LayoutHandleProps,
      } as const);
    }

    return {
      type: 'ORG',
      org,
      saved: Boolean(listingExtras?.[org.id]?.saved),
      locale: serverContext.locale,
      handle: {
        hideFooterDivider: 'hide-footer-divider-if-logged-out',
        searchFromPageType: 'LISTING',
        urlByLocale: org.url,
      } satisfies LayoutHandleProps,
    } as const;
  } catch {
    return idealistNotFound(null);
  }
}

export default function ViewHorizonRoute({
  loaderData,
}: HorizonAppRouteComponentProps<typeof loader>) {
  const { appConfig } = useAppConfig();
  const { i18n } = useI18n();

  switch (loaderData?.type) {
    case undefined:
      return <LazyNotFoundPage />;
    case 'ORG':
      return (
        <RouteModule
          title={`${loaderData.org.name} - ${LISTING_TYPE_HUMAN_NAME_MAP_TITLE(i18n)['ORG']}`}
          ogImage={imageUrlForOgMeta({
            data: loaderData.org.logo,
            appConfig,
          })}
          description={loaderData.org.descriptionTruncated?.slice(0, 200)}
          linkRels={{
            translations: I18N_LOCALES.map((locale) => {
              const url = loaderData.org.url[locale];
              if (url) return { url, locale };
            }).filter(Boolean),
            canonical: loaderData.org.url[loaderData.org.locale],
          }}
          schemaMarkups={[
            loaderData.org.schemaOrgMarkup?.[loaderData.locale],
            loaderData.org.schemaBreadcrumbListMarkup?.[loaderData.locale],
          ]}
        >
          <ViewOrgPage
            initialOrg={loaderData.org}
            initiallySaved={loaderData.saved}
            isPublished
            isPreview={false}
          />
        </RouteModule>
      );
    case 'UNPUBLISHED_ORG':
      return (
        <RouteModule title={i18n('Listing Unpublished')}>
          <LazyUnpublishedOrgPage unpublishedOrg={loaderData.unpublishedOrg} />
        </RouteModule>
      );
  }
}
